// PROVENANCE: C:\Valentin\src\components\DetailDialog.tsx @ d32c53d (main, H3a), copied 2026-10-11 (H3b-6, Ask Olivia)
// CHANGES: Olivia's dictionary (useLanguage, common.close) and colours (the ol-* tokens); named HelpDialog, since only Ask Olivia
//          uses it; the focus effect names its ref as a dependency (Olivia's lint); the rest is Valentin's text, whose own header below names Gap Assessor's panel as its origin.
// PROVENANCE: C:\Gap-Assessor\src\components\FrameworkDetailPanel.tsx @ 2023d32 (branch feat/showcase-openrouter), extracted 2026-10-03
// CHANGES: only the dialog is taken (role=dialog, aria-modal, aria-labelledby; focus into it on open; Esc and
//          the Tab trap on the document; focus back to whatever opened it; the page behind kept still), as a
//          generic dialog with a title, a body and a footer; the framework content is not taken. "What Tab
//          can reach" no longer asks for layout (getClientRects), which a closed <details> was the reason for:
//          it skips [hidden], aria-hidden and the inside of a closed <details> directly, so the trap holds in
//          every browser and in tests. Light theme; every string through t(). H3a: an 'xl' size (near the whole
//          window, the whole screen on a phone) and controls beside the close button.

/**
 * DetailDialog — a modal dialog (design §9.3). Keyboard-complete: focus moves to its close button when it
 * opens, Tab and Shift+Tab stay inside it, Esc closes it, and focus returns to the control that opened it.
 */

import { useCallback, useEffect, useId, useRef, type ReactNode, type RefObject } from 'react';
import { X } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageProvider';

const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])', 'select:not([disabled])',
  'textarea:not([disabled])', 'summary', '[tabindex]:not([tabindex="-1"])',
].join(',');

/** Every element Tab can reach inside the dialog, in document order. */
export function focusableIn(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => {
    if (el.closest('[hidden], [aria-hidden="true"]')) return false;
    const details = el.closest('details');
    if (details && !details.open && el.tagName !== 'SUMMARY') return false;
    return true;
  });
}

export interface DetailDialogProps {
  title: string;
  /** A line under the title. */
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  /** The footer's buttons; each gets close(), which also returns focus to the opener. */
  footer?: (close: () => void) => ReactNode;
  /** Wider for documents; the default suits a description. 'xl' (H3a, Ask Valentin): near the whole window on a desktop, the whole screen on a phone. */
  size?: 'md' | 'lg' | 'xl';
  /** Controls beside the close button (H3a: the panel's size toggle). */
  headerActions?: ReactNode;
  /** Where focus goes when the dialog opens (H2: the question field); the close button by default. */
  initialFocus?: RefObject<HTMLElement>;
}

const SIZE_CLASS: Record<NonNullable<DetailDialogProps['size']>, string> = {
  md: 'max-h-[calc(100dvh-2rem)] w-full max-w-2xl rounded-xl sm:max-h-[90vh]',
  lg: 'max-h-[calc(100dvh-2rem)] w-full max-w-4xl rounded-xl sm:max-h-[90vh]',
  xl: 'h-[100dvh] max-h-[100dvh] w-full rounded-none sm:h-[88vh] sm:max-h-[88vh] sm:w-[min(1100px,96vw)] sm:max-w-none sm:rounded-xl',
};

export default function HelpDialog({ title, subtitle, onClose, children, footer, size = 'md', initialFocus, headerActions }: DetailDialogProps) {
  const { t } = useLanguage();
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  // Remember what had focus, so closing can give it back.
  if (openerRef.current === null && typeof document !== 'undefined') {
    openerRef.current = document.activeElement as HTMLElement | null;
  }

  /** Close and return focus to whatever opened the dialog. */
  const close = useCallback(() => {
    const opener = openerRef.current;
    onClose();
    if (opener && opener !== document.body && document.contains(opener)) {
      // After React has removed the dialog.
      window.setTimeout(() => opener.focus(), 0);
    }
  }, [onClose]);

  // Focus into the dialog on open; keep the page behind it from scrolling.
  useEffect(() => {
    (initialFocus?.current ?? closeRef.current)?.focus();
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
    // Once, when the dialog opens: the field to focus is a ref, which never changes.
  }, [initialFocus]);

  // Esc and the Tab trap listen on the document, not on the dialog: a click on plain text inside the dialog
  // can leave focus on <body>, where a handler on the dialog would never hear the key.
  const closeLatest = useRef(close);
  closeLatest.current = close;
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const root = dialogRef.current;
      if (!root) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        closeLatest.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusableIn(root);
      if (items.length === 0) { e.preventDefault(); root.focus(); return; }
      const first = items[0] as HTMLElement;
      const last = items[items.length - 1] as HTMLElement;
      const active = document.activeElement;
      const inside = active instanceof Node && root.contains(active) && active !== root;
      if (e.shiftKey && (!inside || active === first)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (!inside || active === last)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div
      className={`fixed inset-0 z-50 flex items-start justify-center bg-ol-text/50 sm:items-center ${size === 'xl' ? 'p-0 sm:p-4' : 'p-4 sm:p-6'}`}
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-size={size}
        className={`flex ${SIZE_CLASS[size]} flex-col overflow-hidden border border-ol-border bg-white shadow-xl outline-none`}
      >
        <div className="flex items-start gap-3 border-b border-ol-border px-4 py-4 sm:px-6">
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="text-lg font-semibold leading-snug text-ol-text">{title}</h2>
            {subtitle && <p className="text-sm text-ol-text-2">{subtitle}</p>}
          </div>
          {headerActions}
          <button
            ref={closeRef}
            type="button"
            onClick={close}
            aria-label={t('common.close')}
            className="shrink-0 rounded-md p-2 text-ol-text-2 hover:bg-ol-page hover:text-ol-text"
          >
            <X size={20} aria-hidden />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain break-words px-4 py-5 text-sm sm:px-6">
          {children}
        </div>
        {footer && <div className="flex flex-wrap justify-end gap-2 border-t border-ol-border px-4 py-3 sm:px-6">{footer(close)}</div>}
      </div>
    </div>
  );
}
